import { isFunction } from "lodash";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import {
  useLargeScreenMediaQuery,
  useTailwindLgScreenMediaQuery,
  useTailwindMdScreenMediaQuery,
} from "@/react/hooks/useMediaQuery";
import { NotificationsList } from "@circle-react/components/Notifications/NotificationsList";
import {
  bookmarksPath,
  chatRoomsPath,
  chatThreadsPath,
  getRouteWithPrevLocation,
  notificationsFullPagePath,
  notificationsPath,
} from "@circle-react/helpers/urlHelpers";
import { BookmarksList } from "@circle-react-shared/uikit/HeaderV3/RightActionBlock/Bookmarks/BookmarksList";
import { PopoverPortal } from "@circle-react-uikit/PopoverPortal";
import { PlatformMenuItem } from "./PlatformMenuItem";

export interface MemberLinks {
  allDMsBadgeText?: string;
  disabled?: boolean;
  isMessagingEnabled?: boolean;
  notificationBadgeText?: string;
  onNotificationClick?: () => void;
  resetNewNotificationsCount?: () => void;
  shouldShowChatThreadsTab?: boolean;
  unreadThreadsCount?: number;
}

export const MemberLinks = ({
  allDMsBadgeText,
  disabled,
  isMessagingEnabled,
  notificationBadgeText,
  onNotificationClick,
  resetNewNotificationsCount,
  shouldShowChatThreadsTab,
  unreadThreadsCount,
}: MemberLinks) => {
  const history = useHistory();
  const pathname = history.location.pathname;
  const isLgScreen = useLargeScreenMediaQuery();
  const isLgScreenTw = useTailwindLgScreenMediaQuery();
  const isMdScreen = useTailwindMdScreenMediaQuery();

  const isMessagingActive = pathname.includes(chatRoomsPath());
  const isChatThreadsActive = pathname.includes(chatThreadsPath());
  const isNotificationsActive = pathname.includes(notificationsPath());
  const isBookmarksActive = pathname.includes(bookmarksPath());

  return (
    <>
      {!isLgScreenTw &&
        (isMdScreen ? (
          <PlatformMenuItem
            dataTestId="notifications-menu"
            label={t("notifications.page_title")}
            iconName="sidebar-bell"
            badgeText={notificationBadgeText}
            ariaLabel={t("community_sidebar.unread_notifications", {
              count: Number(notificationBadgeText),
            })}
            onClick={() => {
              resetNewNotificationsCount?.();
              history.push(notificationsFullPagePath());
            }}
            isActive={isNotificationsActive}
          />
        ) : (
          <PopoverPortal
            button={
              <PlatformMenuItem
                dataTestId="notifications-menu"
                label={t("notifications.page_title")}
                iconName="sidebar-bell"
                badgeText={notificationBadgeText}
                ariaLabel={t("community_sidebar.unread_notifications", {
                  count: Number(notificationBadgeText),
                })}
                onClick={() => {
                  if (
                    isLgScreen &&
                    isFunction(onNotificationClick) &&
                    !disabled
                  ) {
                    onNotificationClick();
                  }
                }}
                isActive={isNotificationsActive}
              />
            }
            onOpen={resetNewNotificationsCount}
            disabled={disabled || !isFunction(onNotificationClick)}
          >
            <div
              className="flex size-full flex-col"
              data-testid="notifications-drawer"
            >
              <NotificationsList />
            </div>
          </PopoverPortal>
        ))}
      {isMessagingEnabled && !isLgScreenTw && (
        <PlatformMenuItem
          dataTestId="chat-menu"
          label={t("sidebar_all_dms")}
          iconName="16-messages"
          badgeText={allDMsBadgeText}
          ariaLabel={t("community_sidebar.unread_messages", {
            count: Number(allDMsBadgeText),
          })}
          onClick={() =>
            history.push(getRouteWithPrevLocation({ url: chatRoomsPath() }))
          }
          isActive={isMessagingActive}
        />
      )}
      {shouldShowChatThreadsTab && !isLgScreenTw && (
        <PlatformMenuItem
          dataTestId="chat-threads-menu"
          label={t("chat_threads")}
          iconName="16-chat-threads"
          onClick={() => history.push(chatThreadsPath())}
          isActive={isChatThreadsActive}
          badgeText={String(unreadThreadsCount)}
          ariaLabel={t("community_sidebar.unread_messages", {
            count: Number(unreadThreadsCount),
          })}
        />
      )}
      {isLgScreen && (
        <PopoverPortal
          button={
            <PlatformMenuItem
              dataTestId="bookmarks-menu"
              label={t("header_v3.bookmarks")}
              iconName="16-bookmark"
              onClick={() => history.push(bookmarksPath())}
              isActive={isBookmarksActive}
            />
          }
          disabled={disabled}
        >
          <BookmarksList />
        </PopoverPortal>
      )}
    </>
  );
};
